import { useCallback, useEffect, useState } from 'react';
import { OrderService } from '../../services/order/orderService';
import { TutorService } from '../../services/tutor/tutorService';

export type AssignableTutorsReason = 'ok' | 'no_tutors_configured' | 'no_available_on_date';

export interface AssignableTutorsMeta {
    reason: AssignableTutorsReason;
    productsWithoutTutors: { id: string; code: string | null; name: string | null }[];
}

const defaultAssignableMeta: AssignableTutorsMeta = {
    reason: 'ok',
    productsWithoutTutors: [],
};

/*----------------------------------------------------------------------------------------
* EN: Hook to manage tutors fetching and transformation logic.
* ES: Hook para gestionar la lógica de obtención y transformación de empresas.
----------------------------------------------------------------------------------------*/

const useTutors = (initialFilters = {}) => {

    const [data, setData] = useState<any[]>([]);
    const [assignableTutors, setAssignableTutors] = useState<any[]>([]);
    const [assignableMeta, setAssignableMeta] = useState<AssignableTutorsMeta>(defaultAssignableMeta);
    const [isLoadingAssignable, setIsLoadingAssignable] = useState(false);
    const [filters, setFilters] = useState({
        limit: 999999,
        filter_filters: { ...initialFilters }
    });

    const fetchTutors = useCallback(async () => {
        try {
            const service = new TutorService();
            const response = await service.getTutorsForSelect({ limit: 999999 });
            const fetchedData = response.getResponseData()?.data;

            if (fetchedData && fetchedData?.data) {
                const mappedData = fetchedData.data?.map((data: { id: string; name: string; lastName: string; nifCif: string }) => ({
                    value: data.id,
                    label: data.name + ' ' + data.lastName + ' (' + data.nifCif + ')',
                }));
                setData(mappedData);
            }
        } catch (error) {
            console.log('Error fetching tutors:', error);
        }
    }, []);

    const fetchAssignableTutors = useCallback(async (filters: any) => {
        setIsLoadingAssignable(true);
        try {
            const service = new OrderService();
            const response = await service.getAssignablesTutors({
                limit: 999999,
                filter_filters: { ...filters }
            });
            const responseData = response.getResponseData();
            const fetchedData = responseData?.data;

            if (fetchedData) {
                const mappedData = transformTutors(fetchedData);
                setAssignableTutors(mappedData);
                setAssignableMeta({
                    reason: fetchedData.meta?.reason ?? 'ok',
                    productsWithoutTutors: fetchedData.meta?.productsWithoutTutors ?? [],
                });
            } else {
                setAssignableTutors([]);
                setAssignableMeta(defaultAssignableMeta);
            }
        } catch (error) {
            console.log('Error fetching tutors:', error);
            setAssignableTutors([]);
            setAssignableMeta(defaultAssignableMeta);
        } finally {
            setIsLoadingAssignable(false);
        }
    }, []);

    /**
     * Transforma los datos de los tutores para mostrar en el select de asignación de contratos
     */
    const transformTutors = (data: any) => {
        const categories = ["internal", "external"];

        const mappedData: { value: string; label: string; disabled?: boolean }[] = [];

        categories.forEach(category => {
            if (data[category]?.length > 0) {
                mappedData.push({
                    value: category,
                    label: category === 'internal' ? 'Internos' : 'Externos',
                    disabled: true
                });

                mappedData.push(
                    ...data[category].map((tutor: { id: string; name: string; lastName: string; nifCif: string; priority?: number }) => ({
                        value: tutor.id,
                        label: `Prioridad ${tutor.priority ?? '-'} — ${tutor.name} ${tutor.lastName} (${tutor.nifCif})`,
                    }))
                );
            }
        });

        return mappedData;
    };

    useEffect(() => {
        fetchTutors();
    }, []);

    const updateTutorsHookFilters = (newFilters: any) => {
        setFilters((prevFilters) => ({ ...prevFilters, ...newFilters }));
    };

    const getTutorsList = () => {
        return data;
    };

    const getAssignableTutorsList = () => {
        return assignableTutors;
    };

    const hasAssignableTutors = assignableMeta.reason === 'ok' && assignableTutors.some((item) => !item.disabled);

    return {
        fetchTutors,
        fetchAssignableTutors,
        getTutorsList,
        getAssignableTutorsList,
        updateTutorsHookFilters,
        assignableMeta,
        isLoadingAssignable,
        hasAssignableTutors,
    };
};

export default useTutors;
